/**
 * Graphic Page (图文编辑) — shared TypeScript shape for the "Graphic Page
 * Tree" (page + modules + blocks), used identically by:
 *  - the authenticated Editor (`workspace/website/graphicPages/*`),
 *  - the "Draft Preview" full-page preview (same tree, live rows), and
 *  - the Public Renderer (`public/GraphicPagePublicDetail.tsx`, reading
 *    the immutable `published_snapshot_json`).
 *
 * Mirrors `backend/app/saas/services/graphic_page_service.py`'s
 * `page_dict`/`module_dict`/`block_dict`/`build_page_tree` verbatim — see
 * `docs/migration/LIANGCOLLECTION_GRAPHIC_EDITOR_FULL_AUDIT.md` for the
 * LiangCollection source this was ported from. One canonical schema, no
 * separate "preview schema" / "published schema" (spec requirement #20).
 */

// `home` added in the EXISTING_PAGE_EDITOR_INTEGRATION round — the
// Site's Homepage (formerly only editable via the legacy `PagesTab`
// form) is now a singleton GraphicPage too, so it opens in this same
// canonical editor from the normal "网站 → 图文编辑" entry point. See
// `docs/migration/VISTOERA_EXISTING_PAGE_EDITOR_INTEGRATION.md`.
export const GRAPHIC_PAGE_TYPES = ['home', 'exhibition_landing', 'research_landing', 'collection_landing', 'custom'] as const
export type GraphicPageType = (typeof GRAPHIC_PAGE_TYPES)[number]

export const GRAPHIC_PAGE_TYPE_LABELS: Record<GraphicPageType, string> = {
  home: '首页',
  exhibition_landing: '展览入口',
  research_landing: '研究入口',
  collection_landing: '主题收藏入口',
  custom: '自定义图文页面',
}

export const GRAPHIC_PAGE_SINGLETON_TYPES: GraphicPageType[] = ['home', 'exhibition_landing', 'research_landing', 'collection_landing']

export type GraphicPageStatus = 'draft' | 'in_review' | 'published' | 'unpublished' | 'archived'

export const GRAPHIC_PAGE_STATUS_LABELS: Record<GraphicPageStatus, string> = {
  draft: '草稿',
  in_review: '审核中',
  published: '已发布',
  unpublished: '已下架',
  archived: '已归档',
}

export const GRAPHIC_PAGE_STATUS_COLORS: Record<GraphicPageStatus, string> = {
  draft: 'default',
  in_review: 'blue',
  published: 'green',
  unpublished: 'orange',
  archived: 'default',
}

/** 1:1 port of LiangCollection's `BlockTypeChoices` minus `page_break`
 * (research-article pagination, NOT_APPLICABLE to entry pages — see
 * audit doc §4). */
export const GRAPHIC_BLOCK_TYPES = [
  'hero',
  'image',
  'text',
  'heading',
  'quote',
  'divider',
  'spacer',
  'collection_item',
  'exhibition_ref',
  'research_ref',
  'featured_collection',
  'featured_exhibition',
  'featured_research',
  'video',
  'audio',
] as const
export type GraphicBlockType = (typeof GRAPHIC_BLOCK_TYPES)[number]

// Terminology decoupling (SOURCE_AUDIT_V2.md §7): LiangCollection's
// museum-domain labels (藏品/展览/研究) are generalized to tenant-neutral
// SaaS labels (条目/专题/文章). The `block_type` identifier strings below
// are UNCHANGED — they're referenced by content_json/DB/published
// snapshots/GraphicTreeRenderer — only the tenant-facing label text
// changed. See MIGRATION_MAP_V2.md row 8.
export const GRAPHIC_BLOCK_TYPE_LABELS: Record<GraphicBlockType, string> = {
  hero: 'Hero 大图',
  image: '图片',
  text: '文字',
  heading: '标题',
  quote: '引用',
  divider: '分隔线',
  spacer: '间隔',
  collection_item: '条目',
  exhibition_ref: '专题卡片',
  research_ref: '文章卡片',
  featured_collection: '精选条目',
  featured_exhibition: '精选专题',
  featured_research: '精选文章',
  video: '视频',
  audio: '音频',
}

export const GRAPHIC_BLOCK_TYPE_GROUPS: { title: string; types: GraphicBlockType[] }[] = [
  { title: '基础', types: ['heading', 'text', 'quote', 'divider', 'spacer'] },
  { title: '媒体', types: ['image', 'hero', 'video', 'audio'] },
  { title: '条目与引用', types: ['collection_item', 'exhibition_ref', 'research_ref'] },
  { title: '精选列表', types: ['featured_collection', 'featured_exhibition', 'featured_research'] },
]

/** Block-type icon glyph shown in the sidebar outline rows — 1:1 port of
 * LiangCollection's `content_editor.js` `blockIconGlyph` map. */
export const GRAPHIC_BLOCK_TYPE_GLYPH: Record<GraphicBlockType, string> = {
  collection_item: '条', image: '图', text: '文', video: '视', audio: '音', quote: '引', heading: '标',
  divider: '—', spacer: '⋯',
  exhibition_ref: '专', research_ref: '文', hero: 'H',
  featured_collection: '条', featured_exhibition: '专', featured_research: '文',
}

export interface GraphicPageBlock {
  id: string
  module_id: string
  block_type: GraphicBlockType
  sort_order: number
  is_visible: boolean
  asset_id: string | null
  linked_entry_id: string | null
  linked_page_id: string | null
  content: Record<string, unknown>
  updated_at: string | null
  // Resolved-at-read-time summaries (backend `block_dict`) for
  // collection_item/exhibition_ref/research_ref (`linked_entry`) and
  // featured_* (`resolved_items`) blocks — same canonical schema for
  // editor + draft preview + published snapshot (spec #20).
  linked_entry: GraphicResolvedEntry | null
  resolved_items: GraphicResolvedEntry[]
  // Public-renderer-only fields, added by public_site.py's
  // `_resolve_graphic_tree_urls` at read time — never present in the
  // authenticated editor tree (which resolves asset URLs via
  // `siteAssetVariantUrl` itself, see GraphicTreeRenderer.tsx).
  asset_url?: string | null
  asset_original_url?: string | null
}

export interface GraphicResolvedEntry {
  id: string
  label: string
  slug: string
  content_type: string
  cover_asset_id: string | null
  cover_url?: string | null
}

export interface GraphicPageModule {
  id: string
  page_id: string
  title: string
  subtitle: string
  intro: string
  anchor_slug: string
  sort_order: number
  is_visible: boolean
  show_in_toc: boolean
  blocks: GraphicPageBlock[]
}

export interface GraphicPage {
  id: string
  workspace_id: string
  site_id: string
  page_type: GraphicPageType
  slug: string
  title: string
  subtitle: string
  summary: string
  author: string
  cover_source_type: 'auto' | 'upload' | 'block'
  cover_asset_id: string | null
  cover_pinned_block_id: string | null
  cover_crop_desktop: CropRect | null
  cover_crop_mobile: CropRect | null
  cover_alt_text: string
  status: GraphicPageStatus
  frontend_visible: boolean
  linked_entry_id: string | null
  published_at: string | null
  unpublished_at: string | null
  published_snapshot_version: number
  created_at: string | null
  updated_at: string | null
  // Public-renderer-only, added at read time (see above).
  cover_url?: string | null
  cover_thumbnail_url?: string | null
}

export interface GraphicPageTree {
  page: GraphicPage
  modules: GraphicPageModule[]
}

/** Normalized crop rect (0–100, percentage of the source image) +
 * optional focal point — same schema as LiangCollection's
 * `theme_hero.py` `REQUIRED_KEYS` (`crop_x/y/width/height` +
 * `focal_x/y`), audit doc §11. */
export interface CropRect {
  crop_x: number
  crop_y: number
  crop_width: number
  crop_height: number
  focal_x?: number
  focal_y?: number
}

export const DEFAULT_CROP_DESKTOP: CropRect = { crop_x: 0, crop_y: 0, crop_width: 100, crop_height: 56.25, focal_x: 50, focal_y: 50 }
export const DEFAULT_CROP_MOBILE: CropRect = { crop_x: 0, crop_y: 0, crop_width: 100, crop_height: 133.33, focal_x: 50, focal_y: 50 }
